import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import ProjectPublicationDomainPanel from "../../components/dashboard/ProjectPublicationDomainPanel";
import ProjectDeploymentHistory from "../../components/dashboard/ProjectDeploymentHistory";
import ProjectAccessManager from "../../components/dashboard/ProjectAccessManager";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import {
  PROJECT_MODULES,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import {
  PROJECT_BUSINESS_GOALS,
  PROJECT_BUSINESS_PROFILES,
  businessGoalsForProfile,
  effectiveProjectCapabilities,
  normalizeProjectBusinessProfile,
  normalizeProjectBusinessGoals,
  normalizeProjectCapabilities,
  projectBusinessGoals,
  projectBusinessProfileDefinition,
} from "../../utils/projectCapabilities";

// BLOCK 7 · Business settings cleanup + domain isolation. STATUS: STATIC_PASS target · STAGING_PENDING.
// Domain mutations stay on the dedicated publication surface; this form no longer submits domains.
const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const MODULE_ICONS = {
  landing: <PublicRoundedIcon />,
  products: <Inventory2RoundedIcon />,
  orders: <ListAltRoundedIcon />,
  ecommerce: <PaymentsRoundedIcon />,
  system: <WarehouseRoundedIcon />,
  stats: <AnalyticsRoundedIcon />,
  webpush: <NotificationsActiveRoundedIcon />,
};

function memberId(member) {
  return member.id || member._id;
}

function memberUser(member) {
  return member.userId || member.user || {};
}

function moduleActionPath(projectId, key) {
  if (key === "landing") return `/dashboard/proyectos/${projectId}/pagina`;
  if (key === "products") return `/dashboard/proyectos/${projectId}/productos`;
  if (key === "orders") return `/dashboard/proyectos/${projectId}/pedidos`;
  if (key === "ecommerce") return `/dashboard/proyectos/${projectId}/configuracion/ecommerce`;
  if (key === "system") return `/dashboard/proyectos/${projectId}/sistema`;
  if (key === "stats") return `/dashboard/proyectos/${projectId}/estadisticas`;
  if (key === "webpush") return `/dashboard/proyectos/${projectId}/configuracion/notificaciones`;
  if (key === "customers" || key === "memberships") return `/dashboard/proyectos/${projectId}/clientes`;
  if (["restaurant_menu", "recipes"].includes(key)) return `/dashboard/proyectos/${projectId}/productos`;
  if (["tables", "table_reservations"].includes(key)) return `/dashboard/proyectos/${projectId}/mesas`;
  if (key === "kitchen_orders") return `/dashboard/proyectos/${projectId}/pedidos?vista=comandas`;
  if (["properties", "leases", "lease_collections"].includes(key)) return `/dashboard/proyectos/${projectId}/inmobiliaria`;
  return "";
}

export default function ProjectDetailPage({ embedded = false, section = "all" }) {
  const isMobileViewport = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const compactMobile = embedded && isMobileViewport;
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadProjects, setActiveProject } = useProjects();
  const canEditTechnical = isSuperAdmin(user);

  const [project, setProject] = React.useState(null);
  const [member, setMember] = React.useState(null);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [members, setMembers] = React.useState([]);
  const [form, setForm] = React.useState({
    name: "",
    plan: "catalogo",
    status: "active",
    modules: normalizeProjectModules({}),
    businessProfile: "",
    businessGoals: [],
    capabilities: normalizeProjectCapabilities({}),
  });
  const [memberForm, setMemberForm] = React.useState({ email: "", role: "viewer" });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [migratingStorefront, setMigratingStorefront] = React.useState(false);
  const [storefrontMigration, setStorefrontMigration] = React.useState(null);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const canManageMercadoPago = canEditTechnical || ["owner", "admin"].includes(member?.role);
  const canManageProjectAccess = canEditTechnical || hasProjectPermission(memberAccess, "project.members.manage");
  const canManageBusinessConfiguration = canEditTechnical || hasProjectPermission(memberAccess, "project.settings.manage");

  React.useEffect(() => {
    if (loading || section !== "general") return;
    const area = new URLSearchParams(location.search).get("area");
    if (!area) return;
    // BLOCK 7 · Domain is a first-class destination. Old deep links are kept
    // compatible but immediately redirected so business settings never render
    // domain/publication controls again.
    if (area === "domain") {
      navigate(`/dashboard/proyectos/${projectId}/configuracion/pagina`, { replace: true });
      return;
    }
    const target = document.getElementById(`project-settings-${area}`);
    if (!target) return;
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, location.search, navigate, projectId, section]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await projectsApi.get(projectId);
      const nextProject = data.project;
      const resolvedAccess = normalizeProjectAccess(data.memberAccess || data.member || {});
      setProject(nextProject);
      setMember(data.member || null);
      setMemberAccess(resolvedAccess);
      if (nextProject) setActiveProject(nextProject);
      setForm({
        name: nextProject.name || "",
        plan: normalizeProjectPlanCode(nextProject.billing?.planCode || nextProject.plan || "catalogo"),
        status: nextProject.status || "active",
        modules: normalizeProjectModules(nextProject.modules),
        businessProfile: normalizeProjectBusinessProfile(nextProject.businessProfile),
        businessGoals: projectBusinessGoals(nextProject),
        capabilities: effectiveProjectCapabilities(nextProject),
      });

      if (canEditTechnical || hasProjectPermission(resolvedAccess, "project.members.manage")) {
        try {
          const membersData = await projectsApi.listMembers(projectId);
          setMembers(membersData.members || []);
        } catch (_membersError) {
          setMembers([]);
        }
      } else {
        setMembers([]);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el proyecto");
    } finally {
      setLoading(false);
    }
  }, [canEditTechnical, projectId, setActiveProject]);

  React.useEffect(() => {
    load();
  }, [load]);

  const change = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const changeBusinessProfile = (value) => {
    const businessProfile = normalizeProjectBusinessProfile(value);
    setForm((previous) => ({
      ...previous,
      businessProfile,
    }));
  };

  const toggleBusinessGoal = (goalKey) => {
    setForm((previous) => {
      const selected = new Set(normalizeProjectBusinessGoals(previous.businessGoals));
      if (selected.has(goalKey)) selected.delete(goalKey);
      else selected.add(goalKey);
      return {
        ...previous,
        businessGoals: Array.from(selected),
      };
    });
  };

  const handleDeploymentProjectChange = React.useCallback(
    (nextProject) => {
      if (!nextProject) return;
      setProject(nextProject);
      setActiveProject(nextProject);
    },
    [setActiveProject]
  );

  const migrateStorefrontGateway = async () => {
    if (!canEditTechnical || !projectId || migratingStorefront) return;

    try {
      setMigratingStorefront(true);
      setStorefrontMigration(null);
      setError("");
      setMessage("");

      let currentProject = project;
      if (String(currentProject?.deployment?.mode || "").toLowerCase() !== "shared") {
        const adoption = await projectsApi.migrateProjectToStorefrontGateway(projectId);
        currentProject = adoption?.project || currentProject;
        if (currentProject) handleDeploymentProjectChange(currentProject);
      }

      const data = await projectsApi.migrateDomainsToStorefrontGateway(projectId);
      const migration = data?.migration || null;
      const nextProject = data?.project || migration?.project || currentProject;
      if (nextProject) handleDeploymentProjectChange(nextProject);

      const results = Array.isArray(migration?.results) ? migration.results : [];
      const migrated = results.filter((item) => item?.migrated === true).length;
      const failed = results.filter((item) => item?.migrated !== true);
      setStorefrontMigration({ migration, migrated, failed });

      if (failed.length) {
        const rolledBack = failed.filter((item) => item?.rolledBack === true).length;
        setError(
          `Migración parcial: ${migrated} dominio${migrated === 1 ? "" : "s"} migrado${migrated === 1 ? "" : "s"}, ${failed.length} falló${failed.length === 1 ? "" : "aron"}${rolledBack ? ` (${rolledBack} con rollback)` : ""}.`,
        );
      } else if (results.length) {
        setMessage(
          `Gateway SEO migrado: ${migrated} dominio${migrated === 1 ? "" : "s"}. Google y WhatsApp ya pueden recibir el HTML del proyecto desde el backend.`,
        );
      } else {
        setMessage("El proyecto no tiene dominios personalizados para migrar.");
      }

      await loadProjects();
    } catch (err) {
      setError(err?.message || "No se pudo migrar el proyecto al gateway SEO del StoreFront.");
    } finally {
      setMigratingStorefront(false);
    }
  };

  const save = async (event) => {
    event?.preventDefault?.();
    if (!canEditTechnical) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        name: form.name,
        status: form.status,
      };
      const data = await projectsApi.update(projectId, payload);
      setProject(data.project);
      setActiveProject(data.project);
      setMessage("Configuración general actualizada.");
      await loadProjects();
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar el proyecto");
    } finally {
      setSaving(false);
    }
  };

  const saveBusinessConfiguration = async () => {
    if (!canManageBusinessConfiguration) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const data = await projectsApi.updateBusinessConfiguration(projectId, {
        businessProfile: form.businessProfile,
        businessGoals: normalizeProjectBusinessGoals(form.businessGoals),
      });
      const nextProject = data.project || project;
      setProject(nextProject);
      setActiveProject(nextProject);
      setForm((previous) => ({
        ...previous,
        businessProfile: normalizeProjectBusinessProfile(nextProject?.businessProfile),
        businessGoals: projectBusinessGoals(nextProject),
        capabilities: effectiveProjectCapabilities(nextProject),
      }));
      setMessage("Perfil y objetivos actualizados.");
      await loadProjects();
    } catch (err) {
      setError(err.message || "No se pudo guardar el perfil del negocio");
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.addMember(projectId, memberForm);
      setMemberForm({ email: "", role: "viewer" });
      setMessage("Colaborador agregado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo agregar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (memberToRemove) => {
    const userInfo = memberUser(memberToRemove);
    const ok = window.confirm(`¿Quitar acceso a ${userInfo.email || "este usuario"}?`);
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.removeMember(projectId, memberId(memberToRemove));
      setMessage("Colaborador eliminado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo eliminar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const activeModules = PROJECT_MODULES.filter((module) => form.modules[module.key]);
  const selectedBusinessProfile = projectBusinessProfileDefinition(form.businessProfile);
  const selectedBusinessGoalKeys = new Set(normalizeProjectBusinessGoals(form.businessGoals));
  const suggestedBusinessGoals = businessGoalsForProfile(form.businessProfile);
  const suggestedBusinessGoalKeys = new Set(suggestedBusinessGoals.map((goal) => goal.key));
  const visibleBusinessGoals = [
    ...suggestedBusinessGoals,
    ...PROJECT_BUSINESS_GOALS.filter(
      (goal) => !suggestedBusinessGoalKeys.has(goal.key),
    ),
  ];
  const explicitBusinessGoalsOutsideProfile = visibleBusinessGoals.filter(
    (goal) => selectedBusinessGoalKeys.has(goal.key) && !suggestedBusinessGoalKeys.has(goal.key),
  );
  const showModules = section === "all" || section === "modules";
  const showGeneral = section === "all" || section === "general";
  const showWeb = section === "web";
  const showAccess = section === "all" || section === "access";
  const subscriptionPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;

  if (loading) {
    return <DashboardLoadingState label="Cargando configuración del proyecto…" minHeight={360} />;
  }

  return (
    <Stack spacing={3}>
      {!embedded && (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {project?.name || "Proyecto"}
            </Typography>
            <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.65)", mt: 0.5 }}>
              {project ? `/${project.slug} · DB: ${project.dbName}` : "Cargando configuración..."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              startIcon={<WorkspacePremiumRoundedIcon />}
              onClick={() => navigate(subscriptionPath)}
              variant="outlined"
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange }}
            >
              {canEditTechnical ? "Plan del cliente" : "Mejorar plan"}
            </Button>
            {form.modules.landing && (
              <Button
                startIcon={<PublicRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Landing
              </Button>
            )}
            {form.modules.products && (
              <Button
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Productos
              </Button>
            )}
            {form.modules.orders && (
              <Button
                startIcon={<ListAltRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pedidos`)}
                variant="contained"
                sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Pedidos
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      {showWeb && !loading ? (
        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
          <CardContent sx={{ p: { xs: 2.25, md: 3.5 } }}>
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Dominio y publicación
                </Typography>
                <Typography variant="body2" sx={{ mt: .45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                  Todo lo relacionado con dirección pública, dominio, DNS y despliegue vive solamente acá.
                </Typography>
              </Box>

              {!project ? (
                <Alert severity="error">No se pudo cargar la configuración de publicación del proyecto.</Alert>
              ) : form.modules.landing ? (
                <>
                  <ProjectPublicationDomainPanel
                    project={project}
                    canManage={canManageBusinessConfiguration}
                    canRetry={canManageMercadoPago}
                    canManageInfrastructure={canEditTechnical}
                    onProjectChange={handleDeploymentProjectChange}
                  />

                  {!compactMobile ? <ProjectDeploymentHistory project={project} /> : null}

                  {canEditTechnical ? (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2.5, borderColor: "rgba(243,90,58,.24)", bgcolor: "rgba(243,90,58,.025)" }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark }}>
                            Migración SEO del StoreFront
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: 0.2, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                            Herramienta técnica del dominio. Migra los dominios personalizados al HTML Gateway sin mezclar esta operación con la configuración del negocio.
                          </Typography>
                          {storefrontMigration?.migration ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.45, fontWeight: 850, color: storefrontMigration.failed?.length ? "warning.main" : "success.main" }}>
                              {storefrontMigration.failed?.length
                                ? `${storefrontMigration.migrated} migrados · ${storefrontMigration.failed.length} con error`
                                : `${storefrontMigration.migrated} dominio${storefrontMigration.migrated === 1 ? "" : "s"} migrado${storefrontMigration.migrated === 1 ? "" : "s"} correctamente`}
                            </Typography>
                          ) : null}
                        </Box>
                        <Button
                          type="button"
                          size="small"
                          disabled={migratingStorefront || !form.modules.landing}
                          startIcon={migratingStorefront ? <WebProfitSpinner size={15} /> : <PublicRoundedIcon />}
                          onClick={migrateStorefrontGateway}
                          variant="outlined"
                          sx={{ flexShrink: 0, borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange, borderColor: "rgba(243,90,58,.38)" }}
                        >
                          {migratingStorefront ? "Migrando…" : "Migrar SEO"}
                        </Button>
                      </Stack>
                    </Paper>
                  ) : null}

                  <Alert
                    severity="info"
                    action={
                      <Button
                        type="button"
                        color="inherit"
                        size="small"
                        onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                        sx={{ textTransform: "none", fontWeight: 900 }}
                      >
                        Ir a Página
                      </Button>
                    }
                  >
                    El diseño, contenido y acceso al editor visual se administran desde Página.
                  </Alert>
                </>
              ) : (
                <Alert severity="info">
                  Este proyecto no tiene habilitado el módulo de página web. No hay publicación ni dominio web para administrar desde esta sección.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {showModules && (
      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Módulos del proyecto
              </Typography>
              <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.62)", mt: 0.5 }}>
                Todos los módulos se eligen y cotizan desde Tu plan. Acá podés consultar cuáles están habilitados.
              </Typography>
            </Box>
            <Chip
              icon={<SettingsSuggestRoundedIcon />}
              label={`${activeModules.length} de ${PROJECT_MODULES.length} activos`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 900, alignSelf: "flex-start" }}
            />
          </Stack>

          {(
            <Alert
              severity="info"
              sx={{ mb: 2.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(subscriptionPath)}
                  sx={{ textTransform: "none", fontWeight: 900 }}
                >
                  Ver módulos y precios
                </Button>
              }
            >
              Tu suscripción actual habilita estos módulos. Revisá precios y contrataciones desde Tu plan.
            </Alert>
          )}

          <Grid container spacing={2}>
            {PROJECT_MODULES.map((module) => {
              const enabled = Boolean(form.modules[module.key]);
              const canOpen = enabled && (module.key !== "ecommerce" || canManageMercadoPago);
              return (
                <Grid item xs={12} md={6} xl={module.key === "stats" ? 12 : 6} key={module.key}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3.5,
                      border: `1px solid ${enabled ? "rgba(243,90,58,0.35)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)"}`,
                      bgcolor: enabled ? "rgba(243,90,58,0.035)" : "var(--wp-panel-surface-soft, #F7F8FB)",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: enabled ? "rgba(243,90,58,0.12)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.06)", color: enabled ? BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.5)" }}>
                            {MODULE_ICONS[module.key] || <SettingsSuggestRoundedIcon />}
                          </Box>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip size="small" label={module.group || "General"} sx={{ fontWeight: 900 }} />
                              <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                                {module.label}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.64)", mt: 0.75 }}>
                              {module.description}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip size="small" label={enabled ? "Activo" : "Sin contratar"} color={enabled ? "success" : "default"} />
                      </Stack>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {module.includes.map((item) => (
                          <Chip key={item} size="small" label={item} variant="outlined" />
                        ))}
                      </Stack>

                      {canOpen && (
                        <Button
                          onClick={() => navigate(moduleActionPath(projectId, module.key))}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          {module.key === "ecommerce" ? "Configurar Mercado Pago" : module.key === "webpush" ? "Administrar notificaciones" : `Abrir ${module.label}`}
                        </Button>
                      )}
                      {!enabled && (
                        <Button
                          startIcon={<WorkspacePremiumRoundedIcon />}
                          onClick={() => navigate(`${subscriptionPath}?feature=${encodeURIComponent(module.label)}`)}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          Elegir en Tu plan
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Button
            type="button"
            onClick={() => navigate(subscriptionPath)}
            variant="contained"
            sx={{ mt: 2.5, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          >
            Administrar módulos en Tu plan
          </Button>
        </CardContent>
      </Card>
      )}

      {(showGeneral || showAccess) && (
      <Grid container spacing={3}>
        {showGeneral && (
        <Grid item xs={12} lg={showAccess ? 7 : 12}>
          <Card id="project-settings-business" elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)", scrollMarginTop: 24 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Tu negocio
                </Typography>
                {compactMobile ? (
                  <ContextualHelp compact title="Qué se configura acá" text="Tipo de negocio y necesidades. La selección de módulos y sus precios se administra desde Tu plan." />
                ) : null}
              </Stack>
              <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: .45, mb: 2, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                Tipo de negocio y necesidades. Nada técnico y nada de dominio.
              </Typography>

              <Box component="form" onSubmit={save}>
                <Stack spacing={2}>
                  {canEditTechnical ? (
                    <>
                      <TextField label="Nombre comercial" value={form.name} onChange={(event) => change("name", event.target.value)} disabled={!canEditTechnical} fullWidth />
                      <TextField select label="Estado operativo" value={form.status} onChange={(event) => change("status", event.target.value)} disabled={!canEditTechnical} fullWidth>
                        <MenuItem value="active">Activo</MenuItem>
                        <MenuItem value="paused">Pausado</MenuItem>
                        <MenuItem value="cancelled">Cancelado</MenuItem>
                      </TextField>
                    </>
                  ) : null}

                  {canEditTechnical ? <Divider /> : null}

                  <Box id="project-settings-capabilities" sx={{ scrollMarginTop: 24 }}>
                    <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Tipo y necesidades
                      </Typography>
                      {compactMobile ? (
                        <ContextualHelp compact title="Perfil y módulos" text="El perfil organiza sugerencias. Para contratar o quitar módulos, abrí Tu plan y revisá el total antes de confirmar." />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: 0.45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                      Esto sólo ayuda a WebProfit a priorizar la experiencia. No elimina funciones existentes.
                    </Typography>

                    <TextField
                      select
                      label="Tipo de negocio"
                      value={form.businessProfile}
                      onChange={(event) => changeBusinessProfile(event.target.value)}
                      disabled={!canManageBusinessConfiguration}
                      fullWidth
                      sx={{ mt: 2 }}
                      helperText={selectedBusinessProfile?.description || "Podés dejarlo sin definir y elegir los módulos desde Tu plan."}
                    >
                      <MenuItem value="">Sin perfil definido</MenuItem>
                      {PROJECT_BUSINESS_PROFILES.map((profileOption) => (
                        <MenuItem key={profileOption.key} value={profileOption.key}>
                          {profileOption.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2.5,
                        border: "1px solid rgba(105,87,232,.15)",
                        bgcolor: "rgba(105,87,232,.035)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Qué querés manejar
                      </Typography>
                      <Typography variant="caption" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: 0.25, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", lineHeight: 1.45 }}>
                        Estas son decisiones guardadas del negocio. Cambiar el tipo sólo reordena las sugerencias: no borra lo que ya elegiste.
                      </Typography>

                      {visibleBusinessGoals.length ? (
                        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ mt: 1.1 }}>
                          {visibleBusinessGoals.map((goal) => {
                            const selected = selectedBusinessGoalKeys.has(goal.key);
                            const suggested = suggestedBusinessGoalKeys.has(goal.key);
                            return (
                              <Chip
                                key={goal.key}
                                label={`${goal.label}${!suggested && selected ? " · elegida" : ""}`}
                                clickable={canManageBusinessConfiguration}
                                onClick={canManageBusinessConfiguration ? () => toggleBusinessGoal(goal.key) : undefined}
                                variant={selected ? "filled" : "outlined"}
                                sx={{
                                  fontWeight: 850,
                                  bgcolor: selected ? "rgba(105,87,232,.12)" : "var(--wp-panel-surface, #fff)",
                                  color: selected ? "#6957E8" : BRAND.dark,
                                  borderColor: selected ? "rgba(105,87,232,.38)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)",
                                }}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.9, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                          Podés dejar los objetivos sin definir. Al elegir un tipo de negocio te mostramos sugerencias sin activar nada automáticamente.
                        </Typography>
                      )}

                      {explicitBusinessGoalsOutsideProfile.length ? (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                          {explicitBusinessGoalsOutsideProfile.length === 1
                            ? "Hay una necesidad elegida por vos que no depende de las sugerencias del perfil y se conserva igualmente."
                            : `Hay ${explicitBusinessGoalsOutsideProfile.length} necesidades elegidas por vos que no dependen de las sugerencias del perfil y se conservan igualmente.`}
                        </Typography>
                      ) : null}
                    </Box>

                    {/* # Objetivo: una sola selección comercial, siempre en Tu plan. */}
                    <Alert severity="info" sx={{ mt: 1.5 }} action={
                      <Button color="inherit" size="small" onClick={() => navigate(subscriptionPath)}>
                        Ver módulos y precios
                      </Button>
                    }>
                      Gastronomía, mesas, reservas, comandas, recetas e inmobiliaria son módulos.
                      Elegilos junto con gestión y cobros online desde Tu plan. El perfil del negocio sólo organiza sugerencias.
                    </Alert>

                    {canManageBusinessConfiguration ? (
                      <Button
                        type="button"
                        disabled={saving}
                        startIcon={saving ? <WebProfitSpinner size={16} /> : <SaveRoundedIcon />}
                        onClick={saveBusinessConfiguration}
                        variant="outlined"
                        sx={{ mt: 1.75, borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange, borderColor: "rgba(243,90,58,.35)" }}
                      >
                        {saving ? "Guardando..." : "Guardar preferencias"}
                      </Button>
                    ) : null}
                  </Box>

                  {canEditTechnical ? (
                    <>
                      <Divider />
                      <Box id="project-settings-advanced" sx={{ scrollMarginTop: 24 }}>
                        <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>Administración técnica</Typography>
                        <Typography variant="body2" sx={{ mt: .35, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                          Nombre y estado del proyecto. Los módulos y sus precios se administran desde Tu plan; las excepciones técnicas, desde la administración de WebProfit.
                        </Typography>
                        <Button
                          type="submit"
                          disabled={saving}
                          startIcon={saving ? <WebProfitSpinner size={16} /> : <SaveRoundedIcon />}
                          variant="contained"
                          sx={{ mt: 1.25, alignSelf: "flex-start", bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                        >
                          {saving ? "Guardando..." : "Guardar configuración técnica"}
                        </Button>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {showAccess && (
          <Grid item xs={12} lg={showGeneral ? 5 : 12}>
            {canManageProjectAccess ? (
              <ProjectAccessManager projectId={projectId} currentMember={member} />
            ) : (
              <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Equipo y accesos</Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Solo el owner o un administrador autorizado puede modificar roles, cajas y permisos.
                  </Alert>
                </CardContent>
              </Card>
            )}
          </Grid>
        )}
      </Grid>
      )}
    </Stack>
  );
}
